import React from 'react';
import { ArrowRight, Sparkles, BarChart2, Users, CheckCircle2 } from 'lucide-react';

/**
 * ResearchPathSelector
 *
 * Mostrato dopo l'approvazione di C0 (Research Universe), prima di eseguire il
 * primo contract di ricerca. Permette all'utente di scegliere tra:
 *
 * - Percorso Standard (C1 + C1B): per prodotti con mercato diretto osservabile
 * - Percorso Alternativo (C0X): per innovazioni senza competitor diretti
 */
const ResearchPathSelector = ({ onSelectStandard, onSelectAlternative, executing }) => {
  return (
    <div className="h-full overflow-y-auto bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      <div className="max-w-4xl mx-auto px-6 py-12">

        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm font-medium mb-4">
            <ArrowRight className="w-3.5 h-3.5" />
            Passo successivo
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-3">
            Scegli il percorso di ricerca
          </h1>
          <p className="text-gray-500 max-w-xl mx-auto text-sm leading-relaxed">
            Il tuo prodotto ha già un mercato con competitor osservabili e contenuti UGC disponibili,
            oppure è un'innovazione senza evidenze dirette?
          </p>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

          {/* Standard Path Card */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden flex flex-col">
            <div className="bg-gradient-to-br from-blue-600 to-indigo-600 px-6 py-5 text-white">
              <div className="flex items-center gap-2 mb-2">
                <BarChart2 className="w-5 h-5 text-blue-200" />
                <span className="text-xs font-semibold uppercase tracking-wide text-blue-200">
                  C1 + C1B
                </span>
              </div>
              <h2 className="text-xl font-bold mb-1">Percorso Standard</h2>
              <p className="text-blue-100 text-sm">
                Mercato esistente con evidenze dirette
              </p>
            </div>

            <div className="px-6 py-5 flex-1 flex flex-col">
              <p className="text-sm text-gray-600 mb-5 leading-relaxed">
                Il tuo prodotto opera in un mercato già esistente.
                Esistono competitor, UGC osservabili e conversazioni attive dei consumatori
                che il sistema può analizzare direttamente.
              </p>

              <div className="mb-6">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                  Usa questo percorso se…
                </p>
                <ul className="space-y-2">
                  {[
                    'Esistono già competitor diretti o indiretti',
                    'Trovi annunci e contenuti UGC nel tuo mercato',
                    'I consumatori discutono già di prodotti simili',
                    'La categoria prodotto è riconoscibile e consolidata',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                      <CheckCircle2 className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mb-5 bg-blue-50 rounded-lg px-4 py-3 border border-blue-100">
                <p className="text-xs font-semibold text-blue-700 mb-1.5">Modules eseguiti</p>
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2 text-sm text-blue-800">
                    <span className="w-6 h-5 bg-blue-600 text-white rounded text-xs flex items-center justify-center font-mono font-bold">C1</span>
                    Market Research — UGC, annunci, creator
                  </div>
                  <div className="flex items-center gap-2 text-sm text-blue-800">
                    <span className="w-6 h-5 bg-indigo-600 text-white rounded text-xs flex items-center justify-center font-mono font-bold">C1B</span>
                    Consumer Research — recensioni, conversazioni
                  </div>
                </div>
              </div>

              <button
                onClick={onSelectStandard}
                disabled={executing}
                className="mt-auto w-full flex items-center justify-center gap-2 px-5 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Usa Percorso Standard
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Alternative Path Card */}
          <div className="bg-white rounded-2xl border border-purple-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden flex flex-col">
            <div className="bg-gradient-to-br from-purple-600 to-violet-600 px-6 py-5 text-white">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-5 h-5 text-purple-200" />
                <span className="text-xs font-semibold uppercase tracking-wide text-purple-200">
                  C0X · Percorso Alternativo
                </span>
              </div>
              <h2 className="text-xl font-bold mb-1">Cognitive Prior Transfer</h2>
              <p className="text-purple-100 text-sm">
                Per innovazioni senza evidenze di mercato dirette
              </p>
            </div>

            <div className="px-6 py-5 flex-1 flex flex-col">
              <p className="text-sm text-gray-600 mb-5 leading-relaxed">
                Il tuo prodotto è un'innovazione o opera in un mercato non ancora sviluppato.
                Il sistema ricostruisce i modelli cognitivi del consumatore trasferendoli
                da domini analoghi osservabili, senza simulare un mercato inesistente.
              </p>

              <div className="mb-6">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                  Usa questo percorso se…
                </p>
                <ul className="space-y-2">
                  {[
                    'Non esistono competitor diretti osservabili',
                    'Il prodotto o la categoria non esiste ancora nel mercato',
                    'Non trovi UGC o annunci nel tuo settore specifico',
                    'Il prodotto richiede un cambiamento di comportamento radicale',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                      <CheckCircle2 className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mb-5 bg-purple-50 rounded-lg px-4 py-3 border border-purple-100">
                <p className="text-xs font-semibold text-purple-700 mb-1.5">Modules eseguiti</p>
                <div className="flex items-center gap-2 text-sm text-purple-800">
                  <span className="px-2 h-5 bg-purple-600 text-white rounded text-xs flex items-center justify-center font-mono font-bold">C0X</span>
                  Cognitive Prior Transfer Research Engine
                </div>
                <p className="text-xs text-purple-600 mt-1.5 leading-relaxed">
                  Produce output equivalente a C1+C1B tramite trasferimento cognitivo da domini analoghi
                </p>
              </div>

              <button
                onClick={onSelectAlternative}
                disabled={executing}
                className="mt-auto w-full flex items-center justify-center gap-2 px-5 py-3 bg-gradient-to-r from-purple-600 to-violet-600 text-white rounded-xl font-semibold hover:from-purple-700 hover:to-violet-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Usa Percorso Alternativo
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Footer note */}
        <p className="text-center text-xs text-gray-400 mt-8">
          Entrambi i percorsi producono output strutturalmente equivalenti — i contract successivi (C1.5 in poi) funzionano allo stesso modo indipendentemente dalla scelta.
        </p>
      </div>
    </div>
  );
};

export default ResearchPathSelector;
